import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { HOSTING_PLANS, STORE_PLANS } from '@wranglr/shared';
import { useOptionalAuth } from '../hooks/useAuth';
import { useTheme } from '../providers/ThemeContext';
import { ThemeSwitcher } from '../components/common/ThemeSwitcher';
import {
    ShieldCheck,
    Zap,
    Server,
    Globe,
    CheckCircle2,
    XCircle,
    Rocket,
    Activity,
    CreditCard,
    Pencil,
    Lock,
    RefreshCw,
    MapPin,
    Split,
    Archive,
    Terminal
} from 'lucide-react';
import './LandingPage.css';

export default function LandingPage() {
    const auth = useOptionalAuth();
    const user = auth?.user;
    const { theme } = useTheme();
    const [isVisible, setIsVisible] = useState(true);
    const [lastScrollY, setLastScrollY] = useState(0);
    const [storeLicensesEnabled, setStoreLicensesEnabled] = useState(false);

    useEffect(() => {
        fetch('https://api.wranglr.co.za/api/platform-config')
            .then(r => r.json())
            .then(data => {
                if (data.success) setStoreLicensesEnabled(!!data.config?.store_licenses_enabled);
            })
            .catch(() => {});
    }, []);

    useEffect(() => {
        const handleScroll = () => {
            const currentScrollY = window.scrollY;

            if (currentScrollY < lastScrollY || currentScrollY < 50) {
                setIsVisible(true);
            } else if (currentScrollY > lastScrollY && currentScrollY > 50) {
                setIsVisible(false);
            }

            setLastScrollY(currentScrollY);
        };

        window.addEventListener('scroll', handleScroll, { passive: true });
        return () => window.removeEventListener('scroll', handleScroll);
    }, [lastScrollY]);

    // Schema.org Structured Data
    const schemaData = {
        "@context": "https://schema.org",
        "@type": "SoftwareApplication",
        "name": "Wranglr",
        "applicationCategory": "DeveloperApplication",
        "operatingSystem": "Web",
        "offers": {
            "@type": "Offer",
            "price": "99.00",
            "priceCurrency": "ZAR"
        },
        "description": "High-performance static site hosting for South African businesses. Deploy instantly from CLI or drag-and-drop.",
        "aggregateRating": {
            "@type": "AggregateRating",
            "ratingValue": "4.8",
            "ratingCount": "124"
        }
    };

    return (
        <div className="landing">
            <header className={`landing-header ${!isVisible ? 'header-hidden' : ''}`}>
                <div className="container">
                    <img
                        src={theme === 'business' ? '/lightlogo.png' : '/darklogo.png'}
                        alt="Wranglr"
                        className="landing-logo"
                    />
                    <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(schemaData) }} />
                    <nav className="nav">
                        {user ? (
                            <>
                                <span className="nav-text" style={{ marginRight: '1rem', color: '#666' }}>{user.email}</span>
                                <div style={{ marginRight: '1rem' }}>
                                    <ThemeSwitcher />
                                </div>
                                <Link to="/dashboard" className="btn btn-primary">
                                    <span className="desktop-text">Dashboard</span>
                                    <span className="mobile-text">Go</span>
                                </Link>
                            </>
                        ) : (
                            <>
                                <div style={{ marginRight: '1rem' }}>
                                    <ThemeSwitcher />
                                </div>
                                <Link to="/login" className="nav-link">Login</Link>
                                <Link to="/register" className="btn btn-primary">
                                    <span className="desktop-text">Start Free</span>
                                    <span className="mobile-text">Start</span>
                                </Link>
                            </>
                        )}
                    </nav>
                </div>
            </header>

            {/* 1. Hero: Clear, opinionated, local */}
            <section className="hero">
                <div className="container">
                    <h1 className="hero-title">
                        Fast, zero-maintenance hosting for <span className="gradient-text">South African agencies.</span>
                    </h1>
                    <p className="hero-subtitle">
                        Static sites served from JHB, CPT & DUR. Dynamic features via Serverless APIs.<br />
                        No servers. No plugins. No DevOps.
                    </p>
                    <div className="hero-actions">
                        {user ? (
                            <Link to="/dashboard" className="btn btn-primary btn-lg">
                                Go to Dashboard →
                            </Link>
                        ) : (
                            <Link to="/register" className="btn btn-primary btn-lg">
                                Start Free Trial →
                            </Link>
                        )}
                        <a href="#how-it-works" className="btn btn-secondary btn-lg">
                            See how it works
                        </a>
                    </div>
                </div>
            </section>

            {/* 2. The Enemy: What problem are we killing? */}
            <section className="contrast-section" id="how-it-works">
                <div className="container">
                    <div className="contrast-grid">
                        <div className="contrast-card bad">
                            <h3><XCircle size={24} className="icon-inline error" /> The Old Way</h3>
                            <ul>
                                <li>Complex VPS & Nginx setup</li>
                                <li>Slow overseas hosting (latency)</li>
                                <li>Plugin updates break sites</li>
                                <li>Paying for idle CPU usage</li>
                                <li>"My site is down" phone calls</li>
                            </ul>
                        </div>
                        <div className="contrast-card good">
                            <h3><Zap size={24} className="icon-inline success" /> The Wranglr Way</h3>
                            <ul>
                                <li>Zero-config Static + Edge</li>
                                <li>Local latency (JHB/CPT/DUR)</li>
                                <li>No database or plugins to hack</li>
                                <li>Unbeatable "Core Web Vitals"</li>
                                <li>One predictable bill</li>
                            </ul>
                        </div>
                    </div>
                </div>
            </section>

            {/* 3. The 3 Pillars */}
            <section className="pillars-section">
                <div className="container">
                    <div className="features-grid three-col">
                        <div className="feature-card">
                            <div className="feature-icon"><ShieldCheck size={40} /></div>
                            <h3>Zero Maintenance</h3>
                            <p>No servers. No patching. No runtime exploits. Just upload your code and we handle the scaling automatically.</p>
                        </div>
                        <div className="feature-card">
                            <div className="feature-icon"><MapPin size={40} /></div>
                            <h3>SA-First Performance</h3>
                            <p>Edge-cached in JHB, CPT, and DUR. Better Core Web Vitals and faster SEO results for your local traffic.</p>
                        </div>
                        <div className="feature-card">
                            <div className="feature-icon"><CreditCard size={40} /></div>
                            <h3>Agency Economics</h3>
                            <p>Host 25 sites for R1499/mo. Predictable costs mean higher margins on your monthly retainers.</p>
                        </div>
                    </div>
                </div>
            </section>

            {/* 4. The "Dynamic" Objection */}
            <section className="dynamic-section">
                <div className="container">
                    <div className="dynamic-content">
                        <h2>"But what about forms & logins?"</h2>
                        <p className="lead">Need dynamic features? <strong>Connect to any modern backend.</strong></p>
                        <div className="dynamic-grid">
                            <div className="arch-step">
                                <span className="step-badge">Frontend</span>
                                <h4>Wranglr (Edge)</h4>
                                <p>Serves your HTML/JS instantly from local PoPs.</p>
                            </div>
                            <div className="arch-arrow">→</div>
                            <div className="arch-step">
                                <span className="step-badge">Backend</span>
                                <h4>API / Database</h4>
                                <p>Supabase, Firebase, Turso, or your own API.</p>
                            </div>
                        </div>
                        <p className="dynamic-note">This separates your concerns. You get a clean frontend and a powerful, serverless backend. No PHP spaghetti.</p>
                    </div>
                </div>
            </section>

            {/* 5. Trust & Targeting */}
            <section className="trust-section">
                <div className="container">
                    <h2 className="section-title">Who is this for?</h2>
                    <div className="trust-grid">
                        <div className="trust-col perfect-for">
                            <h3><CheckCircle2 size={24} className="icon-inline success" /> Perfect For</h3>
                            <ul>
                                <li>Agency marketing sites</li>
                                <li>Client landing pages</li>
                                <li>Portfolios & Beacons</li>
                                <li>Lightweight Web Apps (React/Vue)</li>
                            </ul>
                        </div>
                        <div className="trust-col not-for">
                            <h3><XCircle size={24} className="icon-inline error" /> Not For</h3>
                            <ul>
                                <li>WooCommerce clones</li>
                                <li>Plugin-heavy WordPress sites</li>
                                <li>Monolithic SSR apps</li>
                                <li>Legacy PHP hosting</li>
                            </ul>
                        </div>
                    </div>
                </div>
            </section>

            {/* 6. Magical Features */}
            <section className="magical-section">
                <div className="container">
                    <h2 className="section-title">Built for Speed</h2>
                    <div className="magical-grid">
                        <div className="magic-item">
                            <h4><Zap size={20} className="icon-inline" /> Instant Previews</h4>
                            <p>Share a live link with clients <em>before</em> configuring DNS.</p>
                        </div>
                        <div className="magic-item">
                            <h4><Pencil size={20} className="icon-inline" /> In-Browser Edits</h4>
                            <p>Fix a typo on a client's site from your phone, without opening VS Code.</p>
                        </div>
                        <div className="magic-item">
                            <h4><Lock size={20} className="icon-inline" /> Free SSL & CDN</h4>
                            <p>Enterprise-grade security and speed included in every plan.</p>
                        </div>
                        <div className="magic-item">
                            <h4><RefreshCw size={20} className="icon-inline" /> Zero-Downtime</h4>
                            <p>Deployments are atomic. Your site never goes offline during updates.</p>
                        </div>
                        <div className="magic-item">
                            <h4><Split size={20} className="icon-inline" /> A/B Testing</h4>
                            <p>Run experiments on your static sites with one click. Optimization built-in.</p>
                        </div>
                        <div className="magic-item">
                            <h4><Archive size={20} className="icon-inline" /> Site Backups</h4>
                            <p>Automatic backups for peace of mind. Restore any version in seconds.</p>
                        </div>
                    </div>
                </div>
            </section>

            {/* CLI Section */}
            <section className="cli-section">
                <div className="container">
                    <h2 className="section-title"><Terminal size={28} style={{ verticalAlign: 'middle', marginRight: '0.5rem' }} />Deploy from Your Terminal</h2>
                    <p className="lead" style={{ textAlign: 'center', maxWidth: '600px', margin: '0 auto 2rem', color: 'var(--text-secondary)' }}>
                        Install the Wranglr CLI and deploy in seconds. No GUI needed.
                    </p>
                    <div className="cli-demo">
                        <div className="cli-window">
                            <div className="cli-titlebar">
                                <span className="cli-dot red"></span>
                                <span className="cli-dot yellow"></span>
                                <span className="cli-dot green"></span>
                                <span className="cli-titlebar-text">Terminal</span>
                            </div>
                            <div className="cli-body">
                                <code><span className="cli-prompt">$</span> npm install -g @wranglr/cli</code>
                                <code><span className="cli-prompt">$</span> wranglr login --token YOUR_API_TOKEN</code>
                                <code><span className="cli-prompt">$</span> wranglr deploy ./dist --site my-site.co.za</code>
                                <code className="cli-success">✓ Deployed 42 files to the Edge in 3.2s</code>
                            </div>
                        </div>
                    </div>
                    <div className="features-grid three-col" style={{ marginTop: '2rem' }}>
                        <div className="feature-card">
                            <div className="feature-icon"><Rocket size={32} /></div>
                            <h3>One Command Deploy</h3>
                            <p>Recursive directory uploads with automatic file detection. Just point it at your build folder.</p>
                        </div>
                        <div className="feature-card">
                            <div className="feature-icon"><RefreshCw size={32} /></div>
                            <h3>CI/CD Ready</h3>
                            <p>Use API tokens in GitHub Actions, GitLab CI, or any pipeline. Fully scriptable.</p>
                        </div>
                        <div className="feature-card">
                            <div className="feature-icon"><Zap size={32} /></div>
                            <h3>Zero Config</h3>
                            <p>No config files needed. Just install, authenticate, and deploy. It's that simple.</p>
                        </div>
                    </div>
                </div>
            </section>

    <section className="pricing" id="pricing">
        <div className="container">
            <h2 className="section-title">Static Hosting Plans</h2>
            <p className="lead" style={{ textAlign: 'center', marginBottom: '3rem', color: 'var(--text-secondary)' }}>Fast, secure, and hassle-free global edge delivery for your marketing sites.</p>
            <div className="pricing-grid">
                {Object.entries(HOSTING_PLANS).map(([key, plan]) => (
                    <div key={key} className={`pricing-card ${key === 'agency' ? 'featured' : ''}`}>
                        {key === 'agency' && <div className="badge-popular">Agency Favorite</div>}
                        <h3 className="plan-name">{plan.name}</h3>
                        <div className="plan-price">
                            <span className="currency">R</span>
                            <span className="amount">{(plan.price / 100).toFixed(0)}</span>
                            <span className="period">/mo</span>
                        </div>
                        <ul className="plan-features">
                            {plan.features.map((feature, i) => (
                                <li key={i}>✓ {feature}</li>
                            ))}
                        </ul>
                        <Link to={user ? '/dashboard' : '/register'} className="btn btn-primary btn-full">
                            {user ? 'Go to Dashboard' : (key === 'starter' ? 'Start Free Trial' : 'Get Started')}
                        </Link>
                    </div>
                ))}
            </div>

            {storeLicensesEnabled && (
            <>
            <h2 className="section-title" style={{ marginTop: '5rem' }}>E-Commerce Platform</h2>
            <p className="lead" style={{ textAlign: 'center', marginBottom: '3rem', color: 'var(--text-secondary)' }}>Fully managed, multi-tenant online stores with Yoco integration, Courier Guy tracking, and zero downtime.</p>
            <div className="pricing-grid" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))', maxWidth: '800px', margin: '0 auto' }}>
                {Object.entries(STORE_PLANS).map(([key, plan]) => (
                    <div key={key} className="pricing-card" style={{ borderColor: 'var(--primary-color)' }}>
                        <div className="badge-popular" style={{ backgroundColor: 'var(--primary-color)' }}>New Feature</div>
                        <h3 className="plan-name">{plan.name}</h3>
                        <div className="plan-price">
                            <span className="currency">R</span>
                            <span className="amount">{(plan.price / 100).toFixed(0)}</span>
                            <span className="period">/mo</span>
                        </div>
                        <ul className="plan-features">
                            {plan.features.map((feature, i) => (
                                <li key={i}>✓ {feature}</li>
                            ))}
                        </ul>
                        <Link to={user ? '/dashboard' : '/register'} className="btn btn-primary btn-full">
                            {user ? 'Go to Dashboard' : 'Launch Store'}
                        </Link>
                    </div>
                ))}
            </div>
            </>
            )}

            <div className="custom-plan-cta" style={{ textAlign: 'center', marginTop: '4rem', padding: '2rem', background: 'var(--bg-secondary)', borderRadius: '12px', border: '1px solid var(--border-color)' }}>
                <h3>Need a Custom Solution?</h3>
                <p style={{ maxWidth: '600px', margin: '1rem auto', color: 'var(--text-secondary)' }}>
                    Running a massive network of sites or need enterprise-grade SLAs?
                    We offer custom plans with dedicated support and volume pricing.
                </p>
                <a href="mailto:support@wranglr.co.za" className="btn btn-outline">Contact Sales</a>
            </div>
        </div>
    </section>

            {/* 8. Social Proof / About */}
            <section className="social-proof">
                <div className="container">
                    <div className="proof-content">
                        <h3>Built By South African Devs</h3>
                        <p>We designed Wranglr for agencies managing 10 to 100 sites who are tired of dealing with server maintenance. We don't hide behind chatbots. We're real engineers solving real latency problems.</p>
                    </div>
                </div>
            </section>

            {/* 9. Final CTA */}
            <section className="final-cta">
                <div className="container">
                    <h2>Stop babysitting servers.<br />Start shipping fast sites.</h2>
                    <div className="cta-buttons">
                        <Link to={user ? '/dashboard' : '/register'} className="btn btn-primary btn-lg">{user ? 'Go to Dashboard' : 'Get Started'}</Link>
                        <Link to="/docs" className="btn btn-secondary btn-lg">View Documentation</Link>
                    </div>
                </div>
            </section>

            <footer className="landing-footer">
                <div className="container">
                    <div className="footer-links">
                        <Link to="/docs">Documentation</Link>
                        <Link to="/terms">Terms of Service</Link>
                        <Link to="/privacy">Privacy Policy</Link>
                    </div>
                    <p>© 2026 Wranglr.co.za. The High-Performance Static Host.</p>
                </div>
            </footer>
        </div>
    );
}
